import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { popupService, resolveImageUrl } from "../../services/catalogService";
import { trackEvent } from "../../analytics/ga4";
import { useLanguage } from "../../context/LanguageContext";

const DISMISS_KEY = "tessa_popup_dismissed";
const DONE_KEY = "tessa_popup_subscribed";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Task 18 — admin-activatable popup with image + contact capture.
// Shows once per session (and never again after subscribing).
export default function PromoPopup() {
  const { t } = useLanguage();
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(DISMISS_KEY) || localStorage.getItem(DONE_KEY)) return;
    popupService.getPublic().then((cfg) => {
      if (cfg?.enabled) {
        setConfig(cfg);
        window.setTimeout(() => setOpen(true), 2500);
      }
    }).catch(() => {});
  }, []);

  const close = () => {
    setOpen(false);
    sessionStorage.setItem(DISMISS_KEY, "1");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      setError(t("form.errors.email"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      await popupService.subscribe({ name: name.trim(), email: email.trim() });
      trackEvent("generate_lead", { source: "popup" }); // Task 20 — GA4 conversion
      setDone(true);
      localStorage.setItem(DONE_KEY, "1");
      toast.success("Thank you — you're on the list");
    } catch {
      setError("We couldn't save your email. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && config && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] grid place-items-center bg-black/70 p-4"
          data-testid="promo-popup"
          role="dialog"
          aria-modal="true"
          aria-label={config.title}
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-2xl bg-[#F6F2EC] grid grid-cols-1 sm:grid-cols-2 overflow-hidden"
          >
            <button
              onClick={close}
              data-testid="popup-close"
              aria-label={t("common.close")}
              className="absolute top-3 right-3 z-10 grid h-9 w-9 place-items-center bg-white/90 text-[#252525] hover:bg-[#D92A47] hover:text-white transition-colors"
            >
              <X size={16} />
            </button>

            {config.image && (
              <div className="relative h-40 sm:h-auto">
                <img src={resolveImageUrl(config.image)} alt="" className="absolute inset-0 h-full w-full object-cover" />
              </div>
            )}

            <div className="p-8 sm:p-10">
              {done ? (
                <div data-testid="popup-success">
                  <span className="grid place-items-center h-12 w-12 rounded-full bg-[#C4A46B] text-black mb-5">
                    <Check size={22} />
                  </span>
                  <h3 className="font-serif text-3xl text-[#252525]">{t("popup.thanksTitle")}</h3>
                  <p className="text-[#817B74] mt-2 text-sm leading-relaxed">
                    {t("popup.thanksText")}
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="font-serif text-3xl leading-tight text-[#252525]" data-testid="popup-title">
                    {config.title}
                  </h3>
                  {config.text && (
                    <p className="text-[#817B74] mt-3 text-sm leading-relaxed">{config.text}</p>
                  )}
                  <form onSubmit={submit} noValidate className="mt-6 space-y-4" data-testid="popup-form">
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={t("popup.namePlaceholder")}
                      data-testid="popup-name"
                      className="w-full bg-transparent border-b border-black/20 py-2.5 text-sm focus:outline-none focus:border-[#D92A47] placeholder:text-[#817B74]/70"
                    />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setError(""); }}
                      placeholder={t("popup.emailPlaceholder")}
                      data-testid="popup-email"
                      aria-invalid={Boolean(error)}
                      className="w-full bg-transparent border-b border-black/20 py-2.5 text-sm focus:outline-none focus:border-[#D92A47] placeholder:text-[#817B74]/70"
                    />
                    {error && <p role="alert" className="text-[#D92A47] text-xs" data-testid="popup-error">{error}</p>}
                    <button
                      type="submit"
                      disabled={busy}
                      data-testid="popup-submit"
                      className="w-full inline-flex items-center justify-center gap-2 h-12 bg-[#D92A47] text-[#F6F2EC] text-[12px] font-semibold uppercase tracking-[0.14em] hover:bg-[#b71e3a] transition-colors disabled:opacity-70"
                    >
                      {busy ? <Loader2 size={15} className="animate-spin" /> : t("popup.subscribe")}
                    </button>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
